import { createClient } from '@supabase/supabase-js';
import { Database } from '../types/database';

// Environment variables configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

// Create Supabase client for production
// Updated to clear cached auth state - July 7, 2025
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    debug: process.env.NODE_ENV === 'development', // Enable debug only in development
  },
  global: {
    fetch: (...args) => {
      // Add a timeout to all fetch requests
      return Promise.race([
        fetch(...args),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Fetch timeout')), 10000)
        )
      ]) as Promise<Response>;
    }
  }
});

// Test connection to Supabase
export const testConnection = async (): Promise<{ ok: boolean; error?: string }> => {
  try {
    // Try a simple auth check first
    await supabase.auth.getSession();

    // Simple ping query - check if we can access any table
    const { error } = await supabase
      .from('profiles')
      .select('id')
      .limit(1)
      .maybeSingle();

    if (error) {
      return { ok: false, error: `DB Error: ${error.message} (Code: ${error.code})` };
    }

    return { ok: true };
  } catch (err: any) {
    return { ok: false, error: err.message || 'Unknown error' };
  }
};

// Helper function to get current user
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// Helper function to get user profile
export const getUserProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  
  if (error) throw error;
  return data;
};

// Helper function to sign out
export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}; 